import { useAppDispatch, useAppSelector } from "@/app/store/store"
import axios from "axios"
import { append, IsLoading, merge, pop, reset, setLoading } from "./slice"
import { useCallback, useEffect, useMemo } from "react"
import { Privilege } from "@/savaradio/common"
import { useStoreRealtime } from "../../realtime/hook"
import { useStoreSession } from "../../session/hook"
import { PartialItem } from "../user/admin/util"

export function useStorePrivilege(init_data?: boolean, init_socket?: boolean) {
  const dispatch = useAppDispatch()
  const { session } = useStoreSession()
  const { connect, status, emit, signin } = useStoreRealtime()
  const privilege_status = status("privilege")
  
  const privileges_group = useAppSelector(state => state.privilege_slice.value)
  const isLoading = useAppSelector(s => s.privilege_slice.isLoading)

  const setIsLoading = useCallback((p: Partial<IsLoading>) => {
    dispatch(setLoading(p))
  }, [dispatch])

  const get_privilege = useCallback(async () => {
    if (isLoading.get) return
    setIsLoading({ get: true })
    try {
      const { data } = await axios.get("/api/crud/privilege", { responseType: "json" })
      dispatch(reset(data || []))
    } catch {
      dispatch(reset([]))
    } finally {
      setIsLoading({ get: false })
    }
  }, [dispatch, setIsLoading, isLoading.get])

  const create = useCallback(async (privilege: PartialItem<Omit<Privilege, "_id" | "description">, "validated">) => {
    setIsLoading({ create: true })
    try {
      const { data } = await axios.post("/api/crud/privilege", privilege, { responseType: "json" })
      if (data) {
        dispatch(append(data))
        if (privilege_status === "signed") emit("privilege", "create", data)
        return data
      }
      return null
    } catch {
      return null
    } finally {
      setIsLoading({ create: false })
    }
  }, [dispatch, emit, privilege_status, setIsLoading])

  const remove = useCallback(async (id: string) => {
    setIsLoading({ delete: true })
    try {
      const { data } = await axios.delete(`/api/crud/privilege/${id}`, { responseType: "json" })
      if (data) {
        dispatch(pop(data))
        if (privilege_status === "signed") emit("privilege", "delete", data)
        return data
      }
      return null
    } catch {
      return null
    } finally {
      setIsLoading({ delete: false })
    }
  }, [dispatch, emit, privilege_status, setIsLoading])

  const update = useCallback(async (id: string, upd: Partial<Privilege>) => {
    setIsLoading({ update: true })
    try {
      const { data } = await axios.patch(`/api/crud/privilege/${id}`, upd, { responseType: "json" })
      if (data) {
        const new_data = { ...data, ...upd }
        dispatch(merge(new_data))
        if (privilege_status === "signed") emit("privilege", "update", new_data)
        return new_data
      }
      return null
    } catch {
      return null
    } finally {
      setIsLoading({ update: false })
    }
  }, [dispatch, emit, privilege_status, setIsLoading])

  const find_sync = useCallback((id: string) => {
    return privileges_group?.find(group => group._id === id)
  }, [privileges_group])

  const privileges = useMemo(() => {
    return privileges_group?.reduce((acc, priv) => acc.concat(priv.privileges), [] as Privilege[]) || []
  }, [privileges_group])

  const privileges_target = useMemo<Privilege[]>(() => {
    if (!session || session.role !== "operator") return []
    return privileges_group?.find(p => p._id === session.id)?.privileges || []
  }, [privileges_group, session])

  const initializing = useMemo(() => {
    return isLoading.get && (!privileges_group || privileges_group.length === 0)
  }, [isLoading.get, privileges_group])

  useEffect(() => {
    if (init_data && !privileges_group && !isLoading.get) {
      get_privilege()
    }
  }, [init_data, privileges_group, get_privilege, isLoading.get])

  useEffect(() => {
    if (init_socket && privilege_status === "disconnected") {
      connect("privilege")
    }
  }, [init_socket, privilege_status, connect])

  useEffect(() => {
    if (privilege_status === "connected" && session) {
      signin("privilege", session)
    }
  }, [privilege_status, session, signin])

  return useMemo(() => ({
    privileges_group,
    privileges,
    privileges_target,
    isLoading,
    initializing,
    crud: { create, remove, update, get: get_privilege },
    find: { sync: find_sync },
  }), [
    privileges_group,
    privileges,
    privileges_target,
    isLoading,
    initializing,
    create,
    remove,
    update,
    get_privilege,
    find_sync
  ])
}